import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  const cookieBannerRef = useRef(null);

  // Restore saved preferences
  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");

    if (!consent) {
      setShowBanner(true);
    }

    setAnalyticsEnabled(consent === "accepted");
  }, []);

  // Track cookie banner height
 // Keep WhatsApp button above the banner and move it down when hidden
useEffect(() => {
  const updatePositions = () => {
    const banner = cookieBannerRef.current;

    const bannerHeight =
      showBanner && banner
        ? banner.getBoundingClientRect().height
        : 0;

    // Update the CSS variable
    document.documentElement.style.setProperty(
      "--cookie-banner-height",
      `${bannerHeight}px`
    );

    // Directly update the WhatsApp button position
    const whatsappButton = document.querySelector(
      ".onbbits-wa-btn"
    );

    if (whatsappButton) {
      whatsappButton.style.setProperty(
        "bottom",
        `${bannerHeight + 10}px`,
        "important"
      );
    }
  };

  // Set the initial position
  updatePositions();

  // Update when the banner size changes
  let resizeObserver;

  if (showBanner && cookieBannerRef.current) {
    resizeObserver = new ResizeObserver(updatePositions);
    resizeObserver.observe(cookieBannerRef.current);
  }

  // Handle the WhatsApp widget being created asynchronously
  const mutationObserver = new MutationObserver(updatePositions);

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Recalculate on viewport resize
  window.addEventListener("resize", updatePositions);

  return () => {
    resizeObserver?.disconnect();
    mutationObserver.disconnect();

    window.removeEventListener("resize", updatePositions);

    document.documentElement.style.setProperty(
      "--cookie-banner-height",
      "0px"
    );

    const whatsappButton = document.querySelector(
      ".onbbits-wa-btn"
    );

    if (whatsappButton) {
      whatsappButton.style.setProperty(
        "bottom",
        "10px",
        "important"
      );
    }
  };
}, [showBanner, showPreferences]);


useEffect(() => {
  const updateWhatsAppPosition = () => {
    const widget = document.querySelector("#onbbits-wa-widget");
    const shadow = widget?.shadowRoot;
    const wrapper = shadow?.querySelector(".onbbits-wa-wrap");
    const button = shadow?.querySelector(".onbbits-wa-btn");

if (button) {
  const isMobile = window.matchMedia("(max-width: 576px)").matches;

  button.style.setProperty(
    "width",
    isMobile ? "42px" : "52px",
    "important"
  );

  button.style.setProperty(
    "height",
    isMobile ? "42px" : "52px",
    "important"
  );

  button.style.setProperty(
    "min-width",
    isMobile ? "42px" : "52px",
    "important"
  );

  button.style.setProperty(
    "min-height",
    isMobile ? "42px" : "52px",
    "important"
  );

  const svg = button.querySelector("svg");

  if (svg) {
    svg.style.setProperty(
      "width",
      isMobile ? "21px" : "26px",
      "important"
    );

    svg.style.setProperty(
      "height",
      isMobile ? "21px" : "26px",
      "important"
    );
  }
}

    if (!wrapper) return false;

    const banner = document.querySelector(".cookie-banner");
    const bannerVisible =
      banner && getComputedStyle(banner).display !== "none";

    const bannerHeight =
      bannerVisible ? banner.getBoundingClientRect().height : 0;

    wrapper.style.setProperty(
      "bottom",
      `${bannerHeight + (bannerVisible ? 10 : 10)}px`,
      "important"
    );

    return true;
  };

  // The external widget may render after this component mounts.
  let attempts = 0;

  const interval = setInterval(() => {
    attempts++;

    if (updateWhatsAppPosition() || attempts >= 30) {
      clearInterval(interval);
    }
  }, 300);

  const observer = new MutationObserver(() => {
    updateWhatsAppPosition();
  });

  const banner = document.querySelector(".cookie-banner");

  if (banner) {
    observer.observe(banner, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });
  }

  return () => {
    clearInterval(interval);
    observer.disconnect();
  };
}, [showBanner]);


  // Save consent and notify GoogleAnalytics
  const savePreferences = (enabled) => {
    const consent = enabled ? "accepted" : "rejected";

    localStorage.setItem("cookieConsent", consent);
    localStorage.setItem(
      "analyticsCookieConsent",
      enabled ? "granted" : "denied"
    );

    setAnalyticsEnabled(enabled);
    setShowBanner(false);
    setShowPreferences(false);

    window.dispatchEvent(new Event("cookieConsentChanged"));

    // Track the choice only when analytics consent is granted.
    // A rejected analytics choice is stored locally, not sent to GA4.
    if (enabled && typeof window.gtag === "function") {
      window.gtag("event", "cookie_consent", {
        consent_choice: "accepted",
        event_category: "cookie_preferences"
      });
    }
  };

  const acceptCookies = () => savePreferences(true);

  const rejectCookies = () => savePreferences(false);

  const openPreferences = () => {
    setAnalyticsEnabled(
      localStorage.getItem("cookieConsent") === "accepted"
    );
    setShowPreferences(true);

    // Only measure this interaction if analytics is already permitted.
    if (
      localStorage.getItem("cookieConsent") === "accepted" &&
      typeof window.gtag === "function"
    ) {
      window.gtag("event", "cookie_preferences_open");
    }
  };

  const closePreferences = () => {
    setShowPreferences(false);
  };


useEffect(() => {
  if (!showPreferences) return;

  const html = document.documentElement;
  const body = document.body;

  const previousHtmlOverflow = html.style.overflow;
  const previousBodyOverflow = body.style.overflow;
  const previousHtmlOverscroll = html.style.overscrollBehavior;
  const previousBodyOverscroll = body.style.overscrollBehavior;

  html.style.overflow = "hidden";
  body.style.overflow = "hidden";

  html.style.overscrollBehavior = "none";
  body.style.overscrollBehavior = "none";

  return () => {
    html.style.overflow = previousHtmlOverflow;
    body.style.overflow = previousBodyOverflow;

    html.style.overscrollBehavior = previousHtmlOverscroll;
    body.style.overscrollBehavior = previousBodyOverscroll;
  };
}, [showPreferences]);

useEffect(() => {
  const handleOpenCookiePreferences = () => {
    openPreferences();
  };

  window.addEventListener(
    "openCookiePreferences",
    handleOpenCookiePreferences
  );

  return () => {
    window.removeEventListener(
      "openCookiePreferences",
      handleOpenCookiePreferences
    );
  };
}, []);

  return (
    <>
      {/* Cookie consent banner */}
      {showBanner && (
        <div className="cookie-banner" ref={cookieBannerRef}>
          <div className="cookie-banner-content">
            <div className="cookie-banner-text">
              <h3 className="card-heading">We use cookies</h3>

              <p className="section-text">
                Essential cookies keep our website working. With your
                permission, we also use analytics cookies to understand
                website usage. Read our{" "}
                <Link to="/cookie-policy">Cookie Policy</Link>.
              </p>
            </div>

            <div className="cookie-banner-actions">
              <button
                type="button"
                className="cookie-btn cookie-manage"
                onClick={openPreferences}
              >
                Manage Preferences
              </button>

               <button
                type="button"
                className="cookie-btn cookie-accept"
                onClick={acceptCookies}
              >
                Accept All
              </button>

              <button
                type="button"
                className="cookie-btn cookie-reject"
                onClick={rejectCookies}
              >
                Reject All
              </button>

            </div>
          </div>
        </div>
      )}

      {/* Reopen preferences after the banner has been dismissed */}
      {!showBanner && !showPreferences && (
        <button
          type="button"
          className="cookie-preferences-reopen"
          onClick={openPreferences}
          aria-label="Manage cookie preferences"
        >
          Cookie Preferences
        </button>
      )}

      {/* Preferences modal */}
      {showPreferences && (
        <div
          className="cookie-modal-overlay"
          onClick={closePreferences}
        >
          <section
            className="cookie-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="cookie-modal-header">
              <h2 id="cookie-modal-title">Cookie Preferences</h2>

              <button
                type="button"
                className="cookie-modal-close"
                onClick={closePreferences}
                aria-label="Close preferences"
              >
                ×
              </button>
            </div>

            <p className="cookie-modal-description">
              Choose which cookies you're happy for us to use.
              Essential cookies are always on — they keep the site
              working. You can change this at any time.
            </p>

            <div className="cookie-category">
              <div className="cookie-category-info">
                <h3>Essential Cookies</h3>
                <p>
                  Required for the website to function and to remember
                  your cookie choice. Cannot be disabled.
                </p>
              </div>

              <span className="cookie-always-on">Always on</span>
            </div>

            <div className="cookie-category">
              <div className="cookie-category-info">
                <h3>Analytics Cookies</h3>
                <p>
                  Help us understand how visitors use our website
                  through Google Analytics. Optional and off by default.
                </p>
              </div>

              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={analyticsEnabled}
                  onChange={(event) =>
                    setAnalyticsEnabled(event.target.checked)
                  }
                  aria-label="Enable analytics cookies"
                />
                <span className="cookie-toggle-slider" />
              </label>
            </div>

            <p className="cookie-modal-footer-text">
              More details are available in our{" "}
              <Link to="/cookie-policy" onClick={closePreferences}>
                Cookie Policy
              </Link>.
            </p>

            <div className="cookie-modal-actions">
              <button
                type="button"
                className="cookie-btn cookie-save"
                onClick={() => savePreferences(analyticsEnabled)}
              >
                Save Preferences
              </button>

              <button
                type="button"
                className="cookie-btn cookie-accept"
                onClick={acceptCookies}
              >
                Accept All
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default CookieConsent;